import { AlertDefaults, AlertSettings, PriorityOption, PriorityOrder, WorkflowSettings } from "src/app/hickory-api";
import { isInteger, toInteger } from "src/app/shared/model/utility";
import { AlertModelService } from "../alert-model.service";

export interface WorkflowSettingsTableViewModel {
  title: string;
  description: string;
  columns: string[];
  dataSource: { col: string, data: string }[];
}

export class AlertWorkflow {

  static readonly PIN = 'pin';
  static readonly LIMITED_DURATION = 'poll4duration';
  static readonly MAX_ATTEMPTS = 'pollntimes';
  static readonly MAX_ATTEMPTS_Settings = 'maxAttempts';
  static readonly PIN_LIMITED_DURATION = AlertWorkflow.PIN + AlertWorkflow.LIMITED_DURATION;
  static readonly PIN_MAX_ATTEMPTS = AlertWorkflow.PIN + AlertWorkflow.MAX_ATTEMPTS;
  static readonly DURATION = "duration";
  static readonly RETRY_DELAY = "retryDelay";

  static readonly DEFAULT_RETRY_DELAY = '1';
  static readonly DEFAULT_MAX_ATTEMPTS = '3';
  static readonly HIGH_MAX_ATTEMPTS = '1000';
  static readonly DEFAULT_LIMITED_DURATION = '5';
  static readonly HIGH_LIMITED_DURATION = '480';

  static readonly FRIENDLY_LIMITED_DURATION = 'Limited Duration';
  static readonly FRIENDLY_DURATION = 'Duration';
  static readonly FRIENDLY_RETRY_DELAY = 'Retry Delay';
  static readonly FRIENDLY_MAX_ATTEMPTS = 'Max Attempts';
  static readonly FRIENDLY_STANDARD = 'Standard';
  static readonly FRIENDLY_PIN = 'Pin';
  static readonly PIN_PROTECTED_SUFFIX = '(Pin Protected)';
  static readonly UNKNOWN = 'Unknown Workflow';
}

export class AlertWorkflowHelper {

  static preparePreview(_service: AlertModelService, preview: AlertDefaults) {
    if (preview?.prioritySettings?.priorityOption === PriorityOption.None) {
      // Ideally this should be cleared out in this case, but there is a weird Hickory behavior that merges
      // template values with the send request values and it can cause unpredictable results. So for now,
      // we'll just leave this as is and let the details page ignore it if it's set to 'None'.
      // preview.prioritySettings = undefined;
    }
  }

  static summaryText(service: AlertModelService, workflow: WorkflowSettings = service?.alertSettings?.workflow): string {
    const workflowKey = workflow?.key;
    if (!workflowKey) return AlertWorkflow.FRIENDLY_STANDARD;
    return this.getSummaryToString(workflow);
  }

  static getSummaryToString(workflow: WorkflowSettings, valueKey?: string, includeType = true): string {
    const key = workflow.key;

    let suffix = '';
    if (valueKey === AlertWorkflow.RETRY_DELAY ||
      valueKey === AlertWorkflow.DURATION ||
      key === AlertWorkflow.LIMITED_DURATION) {
      suffix = 'minute(s)';
    }

    let showPinProtected = false;
    if (key === AlertWorkflow.PIN_LIMITED_DURATION ||
      key === AlertWorkflow.PIN_MAX_ATTEMPTS) {
      showPinProtected = true;
    }

    if (valueKey === AlertWorkflow.MAX_ATTEMPTS_Settings || valueKey === AlertWorkflow.PIN) {
      suffix = '';
    }

    let workflowValueKey = valueKey;
    let defaultValue = ''
    if (!workflowValueKey) {
      switch (key) {
        case AlertWorkflow.LIMITED_DURATION:
          workflowValueKey = AlertWorkflow.DURATION;
          defaultValue = AlertWorkflow.DEFAULT_LIMITED_DURATION
          break;
        case AlertWorkflow.MAX_ATTEMPTS:
          workflowValueKey = AlertWorkflow.MAX_ATTEMPTS_Settings
          defaultValue = AlertWorkflow.DEFAULT_MAX_ATTEMPTS
          break;
      }
    }

    const valueData = workflow?.settings[workflowValueKey] || defaultValue || ''

    return [
      (includeType ? this.getFriendlyWorkflowKey(key) : ''),
      (valueKey ? this.getFriendlyWorkflowKey(valueKey, true) : ''),
      valueData,
      suffix,
      (showPinProtected ? AlertWorkflow.PIN_PROTECTED_SUFFIX : '')
    ].join(' ');
  }

  static getFriendlyWorkflowKey(key: string, withColSpace: boolean = true): string {
    const colonSpace = withColSpace ? ': ' : ''
    switch (key) {
      case AlertWorkflow.LIMITED_DURATION:
        return AlertWorkflow.FRIENDLY_LIMITED_DURATION + colonSpace;
      case AlertWorkflow.MAX_ATTEMPTS:
      case AlertWorkflow.MAX_ATTEMPTS_Settings:
        return AlertWorkflow.FRIENDLY_MAX_ATTEMPTS + colonSpace;
      case AlertWorkflow.DURATION:
        return AlertWorkflow.FRIENDLY_DURATION + colonSpace;
      case AlertWorkflow.RETRY_DELAY:
        return AlertWorkflow.FRIENDLY_RETRY_DELAY + colonSpace;
      case AlertWorkflow.PIN:
        return AlertWorkflow.FRIENDLY_PIN + colonSpace;
      default: return AlertWorkflow.UNKNOWN;
    }
  }

  static getCombinedTitle(service: AlertModelService) {
    const settings = service.alertSettings;
    const usePollWorkflow = service.usePollWorkflow && !!settings.workflow;
    const useVoicePriority = service.useVoicePriority && !!settings.prioritySettings;

    if(usePollWorkflow && useVoicePriority) {
      return 'Alert.WorkflowPriority'
    }
    if(!usePollWorkflow && useVoicePriority) {
      return 'Alert.Priority.Title'
    }
    if(usePollWorkflow && !useVoicePriority) {
      return 'Alert.Workflow.Title'
    }
    return null;
  }

  static getSummaryTableTitle(settings: AlertSettings, includeOrder = false) {
    return this.getSummaryAsListFromSettings(settings, includeOrder)?.join(' ');
  }

  static getSummaryAsListFromModel(service: AlertModelService, includeOrder = false, includeWorkflowSummary = true) {
    return this.getSummaryAsList(service.alertSettings, includeOrder, includeWorkflowSummary && service.usePollWorkflow, service.useVoicePriority);
  }

  static getSummaryAsListFromSettings(settings: AlertSettings, includeOrder = false, includeWorkflowSummary = true): string[] {
    return this.getSummaryAsList(settings, includeOrder, includeWorkflowSummary && !!settings.workflow, !!settings.prioritySettings)
  }

  private static getSummaryAsList(settings: AlertSettings, includeOrder: boolean, includeWorkflowSummary: boolean, includePriorityVoiceSummary: boolean) {
    const usePollWorkflow = !!settings.workflow && !!settings.workflow.key;
    const useVoicePriority = !!settings.prioritySettings && settings.prioritySettings?.priorityOption !== PriorityOption.None;
    const addOrder = useVoicePriority && includeOrder && settings.prioritySettings?.priorityOption === PriorityOption.Override;

    let prioritySummary: string[];
    if (useVoicePriority && includePriorityVoiceSummary) {
      prioritySummary = [`Priority Type: ${this.getFriendlyPriorityType(settings.prioritySettings?.priorityOption)}`];
      if (addOrder) {
        prioritySummary.push(`Priority Order: ${settings.prioritySettings?.priorityOrder?.join(', ')}`);
      }
    }

    let workflowSummary: string;
    if (usePollWorkflow && includeWorkflowSummary) {
      workflowSummary = `Workflow: ${this.summaryText(null, settings.workflow)}`
    }

    const combined = (!!workflowSummary || !!prioritySummary) ? [
      workflowSummary,
      ...(prioritySummary || [])
    ] : null;

    if (!!combined && !workflowSummary) combined.splice(0, 1);

    return combined;
  }

  static getWorkflowSummaryTable(settings: AlertSettings): WorkflowSettingsTableViewModel {

    const settingsData: string[] = [];
    const workflow = settings.workflow;
    const priority = settings.prioritySettings;
    const settingsKeys: string[] = [];
    const dataSource: { col: string, data: string }[] = [];

    if (!!workflow && !!workflow.settings) {
      settingsKeys.push(...Object.keys(workflow.settings));
      for (const key of settingsKeys) {

        // don't show max settings on limited duration workflows since it is set to max and ultimately ignored
        if (workflow.key === AlertWorkflow.LIMITED_DURATION && key === AlertWorkflow.MAX_ATTEMPTS_Settings) {
          continue;
        }

        // don't show duration on max attempts workflows since it is set to max and ultimately ignored
        if (workflow.key === AlertWorkflow.MAX_ATTEMPTS && key === AlertWorkflow.DURATION) {
          continue;
        }

        settingsData.push(this.getSummaryToString(workflow, key, false))
      }
      dataSource.push(
        { col: 'Workflow Type', data: this.getFriendlyWorkflowKey(workflow.key, false) },
        { col: 'Settings', data: settingsData.join(', ') }
      );
    }
    if (priority) {
      const priorityOrder = priority.priorityOption === PriorityOption.Override ?
        priority.priorityOrder.join(', ') : 'Calls will be made according to each contacts preference';


      dataSource.push(
        { col: 'Priority Type', data: this.getFriendlyPriorityType(priority.priorityOption) },
        { col: 'Priority Order', data: priorityOrder }
      );
    }

    return <WorkflowSettingsTableViewModel>{
      title: this.getSummaryTableTitle(settings),
      columns: settingsKeys,
      dataSource,
      description: ''
    }
  }

  static isVoiceRequired(alert: AlertDefaults): boolean {
    let required = false;
    required = required || !!alert.prioritySettings?.priorityOption && alert.prioritySettings?.priorityOption !== PriorityOption.None;
    required = required || !!alert.workflow?.key;
    return required;
  }

  static getFriendlyPriorityType(type: PriorityOption) {
    switch (type) {
      case PriorityOption.Override: return 'Override';
      case PriorityOption.UserPreference: return 'User Preference';
      case PriorityOption.None: return 'None';
    }
    return null;
  }

  static validationMessage(service: AlertModelService, workflow: WorkflowSettings = service?.alertSettings?.workflow): string {
    if (!workflow?.key) return null;
    if (workflow?.key?.startsWith(AlertWorkflow.PIN) === true && !workflow?.settings?.pin) return 'Alert.Workflow.PinRequired';
    if (!!workflow?.key && !!workflow?.settings) {
      let valid = true;
      valid = valid && isInteger(workflow.settings.retryDelay);
      valid = valid && isInteger(workflow.settings.maxAttempts);
      valid = valid && isInteger(workflow.settings.duration);
      valid = valid && toInteger(workflow.settings.duration) > toInteger(workflow.settings.retryDelay);
      if (valid) return null;
    }

    return 'Alert.Workflow.InvalidSettings';
  }

  static validate(service: AlertModelService, workflow: WorkflowSettings = service?.alertSettings?.workflow): string[] {
    const message = this.validationMessage(service, workflow);
    return message ? [message] : [];
  }

  static combinedKey(key: string, pinProtected: boolean): string {
    if (!key) return null;
    const prefix = pinProtected ? AlertWorkflow.PIN : '';
    return prefix + key;
  }

  static combinedName(key: string, pinProtected: boolean): string {
    if (!key) return null;
    const prefix = pinProtected ? 'PinProtected' : '';
    return prefix + 'RollingRetry';
  }

  static getDefaultSettings(key: string, pinProtected: boolean): { [key: string]: string; } {
    if (!key) return null;
    const settings: { [key: string]: string; } = {};

    if (pinProtected) settings.pin = '';
    else delete settings.pin;
    settings.retryDelay = AlertWorkflow.DEFAULT_RETRY_DELAY;
    settings.maxAttempts = key === AlertWorkflow.MAX_ATTEMPTS ? AlertWorkflow.DEFAULT_MAX_ATTEMPTS : AlertWorkflow.HIGH_MAX_ATTEMPTS;
    settings.duration = key === AlertWorkflow.LIMITED_DURATION ? AlertWorkflow.DEFAULT_LIMITED_DURATION : AlertWorkflow.HIGH_LIMITED_DURATION;
    return settings;
  }

  static updatedWorkflow(workflow: WorkflowSettings, key: string, pinProtected: boolean): WorkflowSettings {
    if (!key) return null;
    workflow = workflow ?? {};
    workflow.name = this.combinedName(key, pinProtected);
    workflow.key = this.combinedKey(key, pinProtected);
    workflow.version = workflow.version ?? '1.0';
    workflow.triggers = workflow.triggers ?? ['Initialize', 'Gather', 'Hangup'];
    workflow.settings = workflow.settings ?? this.getDefaultSettings(key, pinProtected);
    return workflow;
  }

  static setVoicePriority(service: AlertModelService, option: PriorityOption, order: Array<PriorityOrder>) {
    if (service.useVoicePriority && !!order?.length && !!option && option !== PriorityOption.None) {
      service.alertSettings.prioritySettings = {
        priorityOrder: order,
        priorityOption: option
      }
    } else {
      service.alertSettings.prioritySettings = undefined;
    }
  }
}
